/**
 * ProviderDetailsReviews Component
 * Phase 9: Google Reviews Integration (T141-T145)
 * 
 * Displays provider reviews from Google Places API with proper error handling.
 * 
 * Features:
 * - T141: Displays averageRating from API
 * - T142: Displays googleReviews array from API
 * - T143: Shows "No reviews available" when placeId is NULL
 * - T144: Shows error message when Google Places API fails
 * - T145: Displays review author names, ratings, dates, and text correctly
 */

import { useState } from 'react';
import { Star, AlertCircle } from 'lucide-react';
import { Card } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import type { GoogleReview, ProviderProfile } from '../types/provider-profile.types';

type ProviderDetailsReviewsProps = {
  provider: ProviderProfile;
};

type ReviewAuthorAvatarProps = {
  review: GoogleReview;
};

const getAuthorInitial = (authorName: string): string => {
  return authorName.trim().charAt(0).toUpperCase() || '?';
};

const ReviewAuthorAvatar = ({ review }: ReviewAuthorAvatarProps) => {
  const [imageFailed, setImageFailed] = useState(false);

  if (review.authorPhotoUrl && !imageFailed) {
    return (
      <img
        src={review.authorPhotoUrl}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setImageFailed(true)}
        className="w-10 h-10 rounded-full object-cover"
      />
    );
  }

  return (
    <div
      aria-label={`${review.authorName} avatar`}
      className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium"
    >
      {getAuthorInitial(review.authorName)}
    </div>
  );
};

/**
 * Renders provider reviews from Google Places API
 * 
 * @param provider - Provider profile with Google Reviews data
 */
export const ProviderDetailsReviews = ({
  provider,
}: ProviderDetailsReviewsProps) => {
  // Handle loading state - provider might be undefined initially
  if (!provider) {
    return (
      <Card className="p-6">
        <h2 className="text-xl text-gray-900 mb-6">Client Reviews</h2>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#026857] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reviews...</p>
        </div>
      </Card>
    );
  }

  const { averageRating, totalReviewCount, googleReviews, placeId } = provider;

  // T143: No reviews available when placeId is NULL
  if (!placeId) {
    return (
      <Card className="p-6">
        <h2 className="text-xl text-gray-900 mb-6">Client Reviews</h2>
        <div className="text-center py-12">
          <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Reviews Available
          </h3>
          <p className="text-sm text-gray-600">
            This provider hasn't been connected to Google Reviews yet
          </p>
        </div>
      </Card>
    );
  }

  // T144: Error state when Google Places API fails (googleReviews is null but placeId exists)
  if (placeId && !googleReviews) {
    return (
      <Card className="p-6">
        <h2 className="text-xl text-gray-900 mb-6">Client Reviews</h2>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Unable to Load Reviews
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            We're having trouble connecting to Google Reviews right now. Please try again later.
          </p>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  // T141: Display averageRating from API
  const rating = averageRating || 0;
  const reviewCount = totalReviewCount || 0;
  const reviews = googleReviews?.reviews || [];

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-xl text-gray-900">Client Reviews</h2>
        </div>

        {/* Rating Summary - T141: Display averageRating from API */}
        <div className="flex items-center gap-8 mb-8 pb-6 border-b">
          <div className="text-center">
            <div className="text-5xl text-gray-900 mb-2">
              {rating.toFixed(1)}
            </div>
            <div className="flex items-center justify-center mb-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${
                    i < Math.floor(rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <div className="text-sm text-gray-500">
              {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
            </div>
          </div>

          <div className="flex-1 rounded-lg bg-gray-50 border border-gray-100 p-4">
            <p className="text-sm font-medium text-gray-900">
              Showing {reviews.length} recent Google review {reviews.length === 1 ? 'highlight' : 'highlights'}
            </p>
            {googleReviews?.googleMapsUrl && (
              <p className="text-sm text-gray-600 mt-1">
                Want to read more feedback?{' '}
                <a
                  href={googleReviews.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-[#026857] hover:underline"
                >
                  View additional reviews on Google
                </a>
                .
              </p>
            )}
          </div>
        </div>

        {/* T142, T145: Display googleReviews array with author names, ratings, dates, and text */}
        {reviews.length > 0 ? (
          <div className="space-y-6">
            {reviews.map((review, idx) => (
              <div key={idx} className="border-b last:border-0 pb-6 last:pb-0">
                {/* Review Header */}
                <div className="flex items-start gap-4 mb-3">
                  <ReviewAuthorAvatar review={review} />

                  <div className="flex-1 min-w-0">
                    {/* Author Name and Date */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">
                        {review.authorName}
                      </span>
                      <span className="text-sm text-gray-500">
                        • {review.timeDescription}
                      </span>
                    </div>

                    {/* Rating Stars */}
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < review.rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Review Text */}
                <p className="text-gray-700 leading-relaxed">
                  {review.text}
                </p>
              </div>
            ))}
          </div>
        ) : (
          /* Empty state when provider has no reviews */
          <div className="text-center py-8">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 text-sm">
              No reviews yet
            </p>
            <p className="text-gray-500 text-sm mt-1">
              Be the first to review this provider
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};
